import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "../chooseride/FoodVendors.css";
import { useCart } from "../chooseride/CartContext";
import { useAuth } from "../../AuthContext";
import AuthButtons from "./AuthButtons";
import ProfileNavDropdown from "./ProfileNavDropdown";
import SubNavbar from "./SubNavbar";
import { db } from "../../firebase";
import { ref, get } from "firebase/database";
import { isClosedToday } from "../chooseride/vendor";

function FoodNavbar({
  address,
  locationType,
  handleOpenAddressModal,
  searchQuery,
  setSearchQuery,
  showBackBtn = false,
  showSubNavbar = false,
  searchPlaceholder = "Search for any cravings...",
}) {
  const navigate = useNavigate();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isFavOpen, setIsFavOpen] = useState(false);
  const [favoriteVendors, setFavoriteVendors] = useState([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const { cart, cartItemCount, clearAllCartData } = useCart();
  const cartRef = useRef(null);
  const auth = useAuth();
  const favRef = useRef(null);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);

  const avatarName =
    auth.riderData?.first_name && auth.riderData?.last_name
      ? `${auth.riderData.first_name} ${auth.riderData.last_name}`
      : auth.currentUser?.displayName || 'Rider';

  const hasFavorites = auth.riderData?.favorites && Object.keys(auth.riderData.favorites).length > 0;

  const groupedCart = useMemo(() => {
    const groups = {};
    cart.forEach(item => {
      if (!item.vendorId) return;

      if (!groups[item.vendorId]) {
        groups[item.vendorId] = {
          vendorInfo: item.vendor || { name: 'Unknown Vendor', imageUrl: '' },
          totalItems: 0,
          totalPrice: 0,
          vendorType: item.vendorType,
        };
      }
      groups[item.vendorId].totalItems += item.quantity;
      groups[item.vendorId].totalPrice += item.totalNumericPrice * item.quantity;
    });
    return Object.entries(groups).map(([vendorId, data]) => ({ vendorId, ...data }));
  }, [cart]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cartRef.current && !cartRef.current.contains(event.target)) {
        setIsCartOpen(false);
      }
      if (favRef.current && !favRef.current.contains(event.target)) {
        setIsFavOpen(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [cartRef, profileDropdownRef, favRef]);

  useEffect(() => {
    if (!isFavOpen || !auth.riderData?.favorites) return;

    const fetchFavoriteVendors = async () => {
        setIsLoadingFavorites(true);
        const favoriteIds = Object.keys(auth.riderData.favorites);
        if (favoriteIds.length === 0) {
            setFavoriteVendors([]);
            setIsLoadingFavorites(false);
            return;
        }

        // 1. Fetch all products once to be efficient.
        const productsSnapshot = await get(ref(db, 'products'));
        const allProductsArray = Object.values(productsSnapshot.val() || {});

        const vendorPromises = favoriteIds.map(async (vendorId) => {
            const favData = auth.riderData.favorites[vendorId];
            const vendorType = favData.type;
            const dbPath = vendorType === 'chef' ? `chefs/${vendorId}` : `vendors/${vendorId}`;
            
            const vendorRef = ref(db, dbPath);
            const snapshot = await get(vendorRef);

            if (snapshot.exists()) {
                const vendorData = snapshot.val();

                // 2. Find the products or specialties for this specific vendor.
                let vendorProducts = [];
                if (vendorType === 'chef') {
                    vendorProducts = vendorData.chefProfile?.specialties || [];
                } else {
                    vendorProducts = allProductsArray.filter(p => p.vendorId === vendorId);
                }

                return { 
                    id: snapshot.key, 
                    ...vendorData, 
                    vendorType,
                    imageUrl: vendorData.image || vendorData.imageUrl,
                    bannerUrl: vendorData.banner || vendorData.bannerUrl,
                    fullProducts: vendorProducts // 3. Attach the products to the vendor object.
                };
            }
            return null;
        });

        const vendors = (await Promise.all(vendorPromises))
            .filter(Boolean)
            .filter(vendor => !isClosedToday(vendor.workingHours));
        setFavoriteVendors(vendors);
        setIsLoadingFavorites(false);
    };

    fetchFavoriteVendors();
  }, [isFavOpen, auth.riderData?.favorites, navigate]);

  const handleLogout = async () => {
    try {
      setIsProfileDropdownOpen(false);
      await auth.signOut();
      // Clear all cart-related data from state and localStorage
      if (clearAllCartData) clearAllCartData();
      // Clear other session/local storage items
      localStorage.removeItem('9troride_address');
      localStorage.removeItem('9troride_locationType');
      sessionStorage.removeItem('9troride_checkoutVendorId');
      sessionStorage.removeItem('9troride_checkoutVendorType');
      // Navigate to a public page after logout, e.g., the food landing page
      navigate('/9troeat');
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  return (
    <>
      <nav className={`vendor-navbar ${showBackBtn ? 'details-navbar' : ''}`}>
        <div className="vendor-navbar-left">
          {showBackBtn ? (
            <button className="details-nav-back-btn" onClick={() => navigate(-1)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
            </button>
          ) : null}
          <div className="vendor-logo" onClick={() => navigate("/9troeat")}>
            <h2>9troEat</h2>
          </div>
        </div>

        <div className="vendor-navbar-center">
          <div className="vendor-mega-search">
            <div className="mega-address" title={address} onClick={handleOpenAddressModal}>
              <span className="mega-address-label">Delivery to</span>
              <span className="mega-address-text">{locationType ? `${locationType} - ` : ''}{address || 'Select Location'}</span>
            </div>
            <div className="mega-divider"></div>
            <div className="mega-input">
              <input 
                type="text" 
                placeholder={searchPlaceholder} 
                value={searchQuery || ""}
                onChange={(e) => setSearchQuery && setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="mega-clear-search-btn" onClick={() => setSearchQuery && setSearchQuery("")}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              )}
              <button className="mega-search-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              </button>
            </div>
          </div>
        </div>

        <div className="vendor-navbar-right">
          <div className="vendor-fav-wrapper" ref={favRef}>
            <div className={`vendor-fav-minimal ${hasFavorites ? 'is-favorite' : ''}`} onClick={() => setIsFavOpen(prev => !prev)}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
            </div>
            {isFavOpen && (
              <div className="cart-dropdown">
                <div className="cart-dropdown-header">
                  <h3>Your Favorites</h3>
                </div>
                <div className="cart-dropdown-body">
                  {isLoadingFavorites ? (
                    <div className="cart-empty">Loading...</div>
                  ) : favoriteVendors.length === 0 ? (
                    <div className="cart-empty">You have no favorites yet.</div>
                  ) : (
                    favoriteVendors.map((vendor) => (
                      <div
                        key={vendor.id}
                        className="cart-vendor-group"
                        onClick={() => {
                          setIsFavOpen(false);
                          const path = vendor.vendorType === 'chef' ? `/9troeat/chef/${vendor.id}` : vendor.vendorType === 'store' ? `/9troeat/store/${vendor.id}` : `/9troeat/restaurant/${vendor.id}`;
                          const selectedLocationString = localStorage.getItem('9troride_selectedLocation');
                          const selectedLocation = selectedLocationString ? JSON.parse(selectedLocationString) : null;
                          navigate(path, { state: { vendor, address, locationType, selectedLocation } });
                        }}
                      >
                        <img src={vendor.imageUrl || vendor.image} alt={vendor.name} className="cart-vendor-image" />
                        <div className="cart-vendor-details">
                          <h4 className="cart-vendor-name">{vendor.name}</h4>
                          <p className="cart-vendor-address">{vendor.address}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="vendor-cart-wrapper" ref={cartRef}>
            <div className="vendor-cart-minimal" onClick={() => setIsCartOpen(!isCartOpen)}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
              {cartItemCount > 0 && <span className="cart-badge-minimal">{cartItemCount}</span>}
            </div>

            {isCartOpen && (
              <div className="cart-dropdown">
                <div className="cart-dropdown-header">
                  <h3>Your Cart</h3>
                  {cartItemCount > 0 && <span className="cart-header-count">{cartItemCount}</span>}
                </div>
                <div className="cart-dropdown-body">
                  {groupedCart.length === 0 ? (
                    <div className="cart-empty">Your cart is empty.</div>
                  ) : (
                    groupedCart.map(({ vendorId, vendorInfo, totalItems, totalPrice, vendorType }) => (
                      <div
                        key={vendorId}
                        className="cart-vendor-group"
                        onClick={() => {
                          setIsCartOpen(false);
                          navigate(`/${vendorType || 'restaurant'}/${vendorId}`, {
                            state: { vendor: vendorInfo, address, locationType }
                          });
                        }}
                      >
                        <img src={vendorInfo.imageUrl} alt={vendorInfo.name} className="cart-vendor-image" />
                        <div className="cart-vendor-details">
                          <h4 className="cart-vendor-name">{vendorInfo.name}</h4>
                          <div className="cart-vendor-summary">
                            <p className="cart-vendor-meta">({totalItems} {totalItems > 1 ? 'items' : 'item'})</p>
                            <div className="cart-vendor-total">₦{totalPrice.toLocaleString()}</div>
                          </div>
                          {address && <p className="cart-vendor-address"><strong>Delivery to:</strong> {address}</p>}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          {auth.currentUser ? (
            <div className="vendor-profile-minimal" ref={profileDropdownRef} style={{ position: 'relative' }}>
              <button 
                onClick={() => setIsProfileDropdownOpen(prev => !prev)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  lineHeight: 0,
                }}
              >
                <img src={auth.currentUser.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(avatarName)}&background=f0f0f0&color=111`} alt="Profile" />
              </button>
              {isProfileDropdownOpen && (
                <ProfileNavDropdown user={auth.currentUser} rider={auth.riderData} onLogout={handleLogout} />
              )}
            </div>
          ) : (
            <AuthButtons variant="minimal" onLinkClick={() => navigate("/login")} />
          )}
        </div>
      </nav>
      {showSubNavbar && <SubNavbar marginTop="90px" />}
    </>
  );
}

export default FoodNavbar;